import React from 'react';
import { Card, Col, Divider, Row, Space, Typography } from 'antd';
import { QuantumVendorDetails, SupplierTitle } from 'models/Supplier';
import SupplierCompetitors from 'components/supplier-dynamics/competitors/SupplierCompetitors';
import 'components/supplier-dynamics/SupplierCard.scss';
import FundingTable from '../FundingTable/FundingTable';
import { ExportOutlined, InfoCircleOutlined, LinkedinFilled, YoutubeFilled } from '@ant-design/icons';
import { SupplierDynamicsVendor } from 'types/global';
import { Acquisitions } from './Acquisitions';
import { QuantumVendorStats } from './QuantumVendorStats';
import { TRUSTED_PARTNER } from 'shared/constants';

const panelMarginBottom = 24;

/**
 * Represents the props for a component.
 * @interface Props
 */
interface Props {
  supplier?: SupplierTitle;
  setSelectedSupplierCardTab: (value: ((prevState: string) => string) | string) => void;
  quantumSupplier?: QuantumVendorDetails;
  toggleValue?: string;
  selectedSupplierCardTab?: string;
  setViewInfo?: ((value: { showPartnerInfo: boolean; showNPIInfo: boolean; showToggle: boolean }) => void) | undefined;
  vendorsList?: Array<SupplierDynamicsVendor>;
}

/**
 * Component to display information about a quantum supplier.
 * @param {Props} props - The properties object containing supplier information.
 * @returns {React.ReactElement} - The rendered component displaying supplier details.
 */
export const AboutQuantumSupplier = (props: Props): React.ReactElement => {
  const {
    quantumSupplier,
    toggleValue,
    selectedSupplierCardTab,
    setViewInfo,
    vendorsList,
    setSelectedSupplierCardTab
  } = props;

  return (
    <Row gutter={24} className="pt-4">
      <Col span={17}>
        <div style={{ marginBottom: panelMarginBottom }} className="panel">
          {/*<div className="panel-header">About {selectedSupplier?.name}</div>*/}
          <div className="panel-header">About {quantumSupplier?.name}</div>
          <div style={{ fontSize: 16 }}>{quantumSupplier?.description}</div>
          <Space className="mt-5 flex">
            {quantumSupplier?.website ? (
              <Space>
                <Typography.Text className="text-gray-500 text-base">Website </Typography.Text>
                <span className="text-base flex items-center">
                  {quantumSupplier.website.replace(/\/$/, '')}
                  <a href={quantumSupplier.website} target="_blank" rel="noopener noreferrer" className="ml-1">
                    <ExportOutlined className="text-xs" />
                  </a>
                </span>
              </Space>
            ) : (
              <Typography.Text className="text-gray-500 text-base">Website is not available</Typography.Text>
            )}
            {(quantumSupplier?.linkedin_link || quantumSupplier?.youtube_link) && (
              <Divider type="vertical" className="h-5 border-gray-400" />
            )}

            {quantumSupplier?.linkedin_link && (
              <a href={quantumSupplier.linkedin_link} target="_blank" rel="noopener noreferrer">
                <LinkedinFilled className="text-blue-600 text-2xl" />
              </a>
            )}
            {quantumSupplier?.youtube_link && (
              <a href={quantumSupplier.youtube_link} target="_blank" rel="noopener noreferrer">
                <YoutubeFilled className="text-red-500 text-2xl" />
              </a>
            )}
          </Space>
          <QuantumVendorStats quantumSupplier={quantumSupplier} />
        </div>
        <FundingTable data={quantumSupplier?.funding} />
        {quantumSupplier?.acquisition && quantumSupplier?.acquisition?.length > 0 && (
          <Acquisitions acquisitions={quantumSupplier?.acquisition} />
        )}
        <Card
          className={'bg-gray-100 mt-5'}
          title={
            <Space className="text-gray-500 font-normal">
              <InfoCircleOutlined />
              <span>
                Supplier Dynamics {TRUSTED_PARTNER}™ <span className="italic">βeta</span>
              </span>
            </Space>
          }
        >
          <h3>
            <span className="font-extrabold">Beta Version</span>: The information displayed above is sourced from 3rd
            party data enrichment providers. We are testing this concept, and{' '}
            <u>you may experience inconsistencies with the data</u> (which is still a work in progress). Let us know if
            you encounter issues, and also what you think of this feature – your feedback will determine our future
            direction! Click the <span className="font-extrabold">Feedback</span> button in the lower-left corner to
            provide input. We appreciate your support as part of the NPI community.
          </h3>
        </Card>
      </Col>
      <Col span={7}>
        {quantumSupplier && (
          <SupplierCompetitors
            toggleValue={toggleValue}
            selectedSupplierCardTab={selectedSupplierCardTab}
            setViewInfo={setViewInfo}
            vendorsList={vendorsList}
            setSelectedSupplierCardTab={setSelectedSupplierCardTab}
          />
        )}
      </Col>
    </Row>
  );
};
